import { isEmpty, set, unset } from 'lodash-es';

/*
 * Credit: https://stackoverflow.com/questions/57907075/vuex-cant-change-deeply-nested-state-data-inside-actions#57910166
 */

let createReactiveNestedObject;

function fillArray(rootArray, arrayElements) {
  arrayElements.forEach((element, i) => {
    if (element instanceof Array) {
      rootArray.push([]);
    } else if (element instanceof Object) {
      rootArray.push({});
    } else {
      rootArray.push(element);
    }

    createReactiveNestedObject(rootArray[i], element);
  });
}

function createReactiveArray(isArray, root, key, values) {
  if (isArray) {
    root.push([]);
  } else {
    root[key] = [];
  }

  fillArray(root[key], values);
}

function createReactiveObject(isArray, obj, key, values) {
  if (isArray) {
    obj.push({});
  } else {
    obj[key] = {};
  }

  createReactiveNestedObject(obj[key], values);
}

function setValue(isArray, obj, key, value) {
  if (isArray) {
    obj.push(value);
  } else {
    obj[key] = value;
  }
}

createReactiveNestedObject = (rootProp, object) => {
  // root is your rootProperty; e.g. state.fullReport
  // object is the entire nested object you want to set

  const root = rootProp;
  const isArray = root instanceof Array;

  // you need to fill Arrays with native Array methods (.push())
  // and Objects with regular assignments in Vuex mutations

  if (object && typeof object === 'object') {
    Object.keys(object).forEach((key) => {
      if (object[key] instanceof Array) {
        createReactiveArray(isArray, root, key, object[key]);
      } else if (object[key] instanceof Object) {
        createReactiveObject(isArray, root, key, object[key]);
      } else {
        setValue(isArray, root, key, object[key]);
      }
    });
  }
};

// When something is cleared out (e.g. the timeline)
// the deepdiff tool will give us an empty object of changes.
// We need to provide fallback values for these lists in case they are cleared.
const ListProperties = ['timeline', 'posts'];

/* eslint-disable no-param-reassign */
const getKeyPaths = (obj, keyPath = [], res = {}) => {
  // End of chain, so set res value and return it
  if (typeof obj !== 'object' || obj === null) {
    res[keyPath.join('.')] = obj;
    return res;
  }

  // Check if value is empty array or object, and just return if that's the case
  if (isEmpty(obj)) {
    if (!keyPath.length) {
      // Nothing changed
      return res;
    }

    const property = keyPath[keyPath.length - 1];
    res[keyPath.join('.')] = ListProperties.includes(property) ? [] : obj;
    return res;
  }

  if (Array.isArray(obj)) {
    for (let i = 0; i < obj.length; i += 1) {
      res = getKeyPaths(obj[i], keyPath.concat([i]), res);
    }
  }

  // Obj is an object (surprise), so go deeper
  Object.keys(obj).forEach((key) => {
    res = getKeyPaths(obj[key], keyPath.concat([key]), res);
  });

  return res;
};
/* eslint-enable no-param-reassign */

/**
 * Merge changes based on deep diff package into source obj
 * @param {Object} obj - Source object to merge values into (e.g. state.production)
 * @param {Object} changes - Object containing added/updated/deleted values
 */
const mergeChanges = (obj, changes) => {
  const { added, deleted, updated } = changes;

  // Loop over added keys and updated keys to merge them in
  const setValues = {
    ...getKeyPaths(added),
    ...getKeyPaths(updated),
  };

  Object.keys(setValues).forEach((key) => {
    // Set deep property first, so we can use it to ensure reactivity
    set(obj, key, setValues[key]);
  });

  // Loop over deleted keys and remove them from state
  const deleteValues = getKeyPaths(deleted);
  Object.keys(deleteValues).forEach((key) => {
    unset(obj, key);
  });

  // I think it's safe to just leave the empty, unset values in the timeline
  // but we can remove them jic in the future:
  // obj.timeline = (obj.timeline || []).filter(Boolean);

  createReactiveNestedObject(obj, obj);
};

export default {
  mergeChanges,
  createReactiveNestedObject,
};
